import { isValidHref } from '../sidebar/sidebarUtils';

/**
 * pathname과 메뉴 노드 href 매칭 점수 (높을수록 우선)
 * - 정확 일치 > prefix 일치, 동급이면 href 길이가 긴 쪽(더 구체적)
 */
function matchScore(pathname, href) {
  if (!pathname || !href || !isValidHref(href)) return -1;
  if (pathname === href) return 1_000_000 + href.length;
  if (pathname.startsWith(`${href}/`)) return href.length;
  return -1;
}

/**
 * 트리에서 pathname에 맞는 최선의 노드까지의 경로(루트→해당 노드)를 수집
 * @param {string} pathname
 * @param {Array<object>} nodes topMenu.menu 배열
 * @returns {object[]|null} 노드 체인 또는 매칭 없음
 */
function findBestMenuNodePath(pathname, nodes) {
  if (!pathname || !Array.isArray(nodes) || nodes.length === 0) return null;

  let bestPath = null;
  let bestScore = -1;

  const walk = (list, ancestors) => {
    if (!list) return;
    for (const node of list) {
      if (!node) continue;
      const chain = [...ancestors, node];
      const href = node.href;
      const sc = matchScore(pathname, href);
      const deeperTie =
        sc >= 0 &&
        sc === bestScore &&
        chain.length > (bestPath?.length ?? 0);
      if (sc > bestScore || deeperTie) {
        bestScore = sc;
        bestPath = chain;
      }
      if (node.children && node.children.length > 0) {
        walk(node.children, chain);
      }
    }
  };

  walk(nodes, []);
  return bestScore >= 0 ? bestPath : null;
}

/**
 * 메뉴 DB에 매핑된 화면이면 title·breadcrumbPath를 DB 트리 기준으로만 생성한다.
 * 규칙: 첫 크럼 HOME 고정, 이후는 매칭 리프의 조상 노드(리프 제외) 순서대로.
 * - label이 비어 있지 않은 조상은 모두 포함한다.
 * - href는 유효할 때만 넣어 링크로; 없으면 `getMenuPath`에서 span(비링크)으로 렌더된다.
 * 매칭 실패·메뉴 없음(`merged: false`)일 때만 페이지 하드코딩(fallback)을 사용한다.
 *
 * @param {string} pathname
 * @param {Array<object>} menuData
 * @param {{ title?: string, breadcrumbPath?: Array<{label?: string, href?: string}>, loading?: boolean }} pagePayload 페이지 하드코딩 값 (fallback 전용)
 * @returns {{ merged: boolean, title: string, breadcrumbPath: Array<{label: string, href?: string}>, loading?: boolean }}
 */
export function mergeBannerWithMenuDb(pathname, menuData, pagePayload) {
  const payload = pagePayload && typeof pagePayload === 'object' ? pagePayload : {};
  const fallbackTitle = payload.title != null ? String(payload.title) : '페이지';
  const fallbackCrumbs = Array.isArray(payload.breadcrumbPath) ? payload.breadcrumbPath : null;
  const loading = payload.loading;

  const chain = findBestMenuNodePath(pathname, menuData);
  if (!chain || chain.length === 0) {
    return {
      merged: false,
      title: fallbackTitle,
      breadcrumbPath: fallbackCrumbs,
      ...(loading !== undefined ? { loading } : {}),
    };
  }

  const leaf = chain[chain.length - 1];
  const leafHref =
    leaf && isValidHref(leaf.href) ? String(leaf.href).split('?')[0] : '';
  const pathnameNorm = pathname ? String(pathname).split('?')[0] : '';
  const isExactPathMatch = Boolean(pathnameNorm && leafHref && pathnameNorm === leafHref);
  /** 메뉴는 /community 등 prefix만 맞고, 페이지가 구체적 breadcrumb을 넘긴 경우 — DB 병합 시 조상이 없어 HOME만 나오는 문제 방지 */
  const hasRichFallback =
    Array.isArray(fallbackCrumbs) &&
    fallbackCrumbs.some((c, i) => i > 0 && c && String(c.label || '').trim() !== '');

  if (!isExactPathMatch && hasRichFallback) {
    return {
      merged: false,
      title: fallbackTitle,
      breadcrumbPath: fallbackCrumbs,
      ...(loading !== undefined ? { loading } : {}),
    };
  }

  const title = (leaf && leaf.label != null && String(leaf.label).trim() !== '')
    ? String(leaf.label)
    : fallbackTitle;

  const crumbs = [{ label: '', href: '/' }];
  const ancestors = chain.slice(0, -1);
  for (const node of ancestors) {
    if (!node) continue;
    const label = node.label != null ? String(node.label).trim() : '';
    if (!label) continue;
    const crumb = { label };
    if (isValidHref(node.href)) {
      crumb.href = String(node.href);
    }
    crumbs.push(crumb);
  }

  const breadcrumbPath = crumbs.length > 0 ? crumbs : [{ label: '', href: '/' }];

  return {
    merged: true,
    title,
    breadcrumbPath,
    ...(loading !== undefined ? { loading } : {}),
  };
}
